export type Locale = "he" | "en";
export type Theme = "light" | "dark";
export type Dir = "rtl" | "ltr";

export type LocaleMeta = {
  id: Locale;
  /** Native display name, shown in the language switcher. */
  label: string;
  abbr: string;
  country: string;
  dir: Dir;
  /** BCP-47 tag used for Intl formatting. */
  intl: string;
};

/**
 * Locale registry. Adding a language (e.g. Arabic/Russian) means adding a row here
 * plus a dictionary below — layout direction and formatting derive from this table.
 */
export const LOCALES: LocaleMeta[] = [
  { id: "he", label: "עברית", abbr: "HE", country: "IL", dir: "rtl", intl: "he-IL" },
  { id: "en", label: "English", abbr: "EN", country: "US", dir: "ltr", intl: "en-GB" },
];

export const DEFAULT_LOCALE: Locale = "he";

export function localeMeta(locale: string | null | undefined): LocaleMeta {
  return LOCALES.find((l) => l.id === locale) ?? LOCALES[0];
}

export function isLocale(v: string | null | undefined): v is Locale {
  return LOCALES.some((l) => l.id === v);
}

export function dirFor(locale: string | null | undefined): Dir {
  return localeMeta(locale).dir;
}

export const LOCALE_KEY = "chatisafe-locale";
export const THEME_KEY = "chatisafe-theme";
export const COOKIE_CONSENT_KEY = "chatisafe-cookie-consent";

const he = {
  nav: {
    how: "איך זה עובד",
    protection: "הגנה",
    plans: "תמחור",
    login: "התחברות",
    logout: "התנתקות",
    startFree: "כאן מתחילים",
    dashboard: "דשבורד",
    mainNav: "ניווט ראשי",
    themeLight: "מצב בהיר",
    themeDark: "מצב כהה",
    language: "שפה",
    openMenu: "פתיחת תפריט",
    closeMenu: "סגירת תפריט",
  },
  footer: {
    tagline: "הגנת וואטסאפ שנבנתה למשפחות. ₪15 לילד — בלי כאבי ראש.",
    privacy: "פרטיות",
    terms: "תנאים",
    accessibility: "הצהרת נגישות",
    links: "קישורים",
  },
  cookieBanner: {
    title: "הודעת עוגיות",
    message:
      "האתר משתמש בעוגיות ובאחסון מקומי הכרחיים לתפעול (התחברות, שפה, נגישות והעדפות). המשך השימוש באתר מהווה הסכמתך לכך.",
    privacyLink: "מדיניות פרטיות",
    accept: "הבנתי",
  },
  a11y: {
    button: "נגישות",
    title: "תפריט נגישות",
    close: "סגירה",
    fontSize: "גודל טקסט",
    increase: "הגדלה",
    decrease: "הקטנה",
    contrast: "ניגודיות גבוהה",
    grayscale: "גווני אפור",
    links: "הדגשת קישורים",
    readable: "גופן קריא",
    spacing: "ריווח טקסט",
    motion: "עצירת אנימציות",
    cursor: "סמן גדול",
    focus: "הדגשת מיקוד מקלדת",
    reset: "איפוס הגדרות",
    statement: "הצהרת נגישות",
    skip: "דילוג לתוכן הראשי",
  },
  landing: {
    h1: "יודעים מה עובר על הילד שלכם ב-WhatsApp?",
    lead: "ChatiSafe עוזרת לזהות שיחות ותכנים שעלולים לדרוש את תשומת לבכם.",
    cta: "כאן מתחילים",
    haveAccount: "כבר יש לי חשבון",
    freeNow: "כרגע ללא עלות",
    seeHow: "איך זה עובד",
    setup: "התקנה ב־60 שניות · חיבור מאובטח לוואטסאפ · סריקה עם פרטיות קודם",
    reality: "המציאות",
    realityH2: "וואטסאפ לא נבנה לילדים.",
    realityP: "הילד צריך אותו כדי להישאר מחובר. אתם צריכים לישון. שני הדברים מתנגשים כל לילה.",
    pain1t: "בריונות והדרה",
    pain1b: "הוצאות מקבוצות והודעות פוגעניות משאירות סימנים שההורים לא רואים בזמן.",
    pain2t: "איומים וזרים",
    pain2b: "מספרים לא מוכרים ולחץ מופיעים הרבה לפני שילד יודע איך להגיב.",
    pain3t: "מדיה לא הולמת",
    pain3b: "תמונות וסרטונים מגיעים בלי אזהרה — ונעלמים לפני שמישהו שואל.",
    how: "איך זה עובד",
    howH2: "מגדירים פעם אחת. נשארים מוגנים.",
    step1t: "יוצרים חשבון הורה",
    step1b: "נרשמים ומוסיפים את הילד הראשון — 3 ימי ניסיון חינם, בלי כרטיס.",
    step2t: "מקשרים את הוואטסאפ של הילד",
    step2b: "סורקים קוד QR מהטלפון של הילד, כמו חיבור וואטסאפ למחשב. לוקח פחות מדקה.",
    step3t: "ההגנה עובדת בשקט",
    step3b: "הודעות נכנסות נבדקות. כשמשהו מדאיג — אתם מקבלים התראה. הילד ממשיך לשוחח כרגיל.",
    prot: "הגנה",
    protH2: "מה בודקים, ומה אתם מקבלים.",
    f1t: "בריונות, איומים ולחץ",
    f1b: "כשמופיעה שפה פוגענית, איום או לחץ חברתי — אתם מקבלים התראה ברורה בוואטסאפ.",
    f2t: "פרטיות קודם",
    f2b: "בודקים רק מה שמגיע לילד. אפשר לבחור לקבל התראה בלי תוכן ההודעה עצמה.",
    f3t: "₪15 לילד. וזהו.",
    f3b: "בלי תוכניות ובלי מדרגות — כל ילד ₪15 ל־30 יום, וכל הילדים מלוח בקרה אחד.",
    f4t: "הדרה מקבוצות",
    f4b: "הוצאה מקבוצה או דפוס של התעלמות — דברים שקשה לראות מבחוץ, ומספרים הרבה.",
    f5t: "זרים ומספרים לא מוכרים",
    f5b: "התראה כשמישהו שלא שמור באנשי הקשר פונה לילד בפעם הראשונה.",
    f6t: "אתם קובעים מה חשוב",
    f6b: "בוחרים על מה להתריע: תמונות, סרטונים, הודעות קוליות, חסימות ועוד — בהגדרות פשוטות.",
    ctaH2: "הגנו על הילד תוך דקות.",
    ctaP: "הילד הראשון ב־3 ימי ניסיון חינם, בלי כרטיס אשראי. אחר כך ₪15 לילד ל־30 יום — חצי שקל ליום. ישנים קצת יותר טוב הלילה.",
    startTrial: "התחילו ניסיון חינם",
    viewPlans: "לתמחור",
    pricing: "תמחור",
    pricingH2: "בלי כאבי ראש. פשוט ₪15 לילד.",
    pricingP:
      "בלי תוכניות, בלי מדרגות ובלי אותיות קטנות. כל ילד — ₪15 ל־30 יום. זה חצי שקל ליום על שקט נפשי.",
    pricingPrice: "₪15",
    pricingUnit: "לילד · ל־30 יום",
    pricingPerDay: "= ₪0.50 ליום",
    pricingPoints: [
      "הילד הראשון — 3 ימי ניסיון חינם, בלי כרטיס אשראי",
      "כל ילד נוסף — ₪15 ל־30 יום, מהרגע הראשון",
      "חידוש אוטומטי או תשלום חד־פעמי — אתם מחליטים",
      "בלי התחייבות: מכבים חידוש אוטומטי בכל רגע. כל תקופה משולמת מראש ל־30 יום ואינה מוחזרת",
    ],
    pricingCta: "מתחילים בניסיון חינם",
  },
  hero: {
    linked: "הוואטסאפ של דנה",
    protected: "מוגן",
    group: "קבוצת הכיתה",
    groupD: "זוהה דפוס של הדרה — נשלחה התראה",
    unknown: "מספר לא מוכר",
    unknownD: "הודעה ראשונה מאדם זר",
    media: "תמונה שהתקבלה",
    mediaD: "נבדקה · לא נמצא דבר מדאיג",
    session: "הגנה",
    live: "פעילה · בודקת הודעות נכנסות",
    parent: "התראה אליכם",
    parentD: "שפה פוגענית בקבוצת הכיתה. הפרטים ומה כדאי לעשות נשלחו בוואטסאפ.",
    child: "אצל הילד",
    childD: "וואטסאפ רגיל. כלום לא משתנה.",
  },
  onboarding: {
    help: "צריכים עזרה?",
    helpTitle: "צריכים עזרה?",
    helpBody: "אפשר לחזור צעד אחד אחורה, או לצאת ולחזור אחר כך. נמשיך בדיוק מהמקום שעצרתם. אין צורך בידע טכני.",
    stepOf: "שלב {step} מתוך {total}",
    welcomeTitle: "בואו נגן על הילד/ה שלכם 💚",
    welcomeLead: "אנחנו נלווה אתכם בכמה צעדים פשוטים. זה לוקח רק כמה דקות.",
    welcomeCta: "בואו נתחיל",
    noTech: "לא צריך ידע טכני.",
    resumeTitle: "היי, נשאר רק להשלים את החיבור של {name}.",
    resumeCta: "להמשיך",
    childTitle: "על מי אנחנו רוצים להגן?",
    childLead: "הוסיפו את הילד או הילדה הראשונים שתרצו לחבר ל-ChatiSafe.",
    childName: "שם הילד/ה",
    childNameError: "הזינו שם לילד/ה",
    childCta: "המשך לחיבור WhatsApp",
    adding: "מוסיפים את {name}…",
    addFail: "לא הצלחנו להוסיף. נסו שוב.",
    needPayTitle: "כדי להוסיף ילד נוסף צריך להמשיך ממסך ההורה",
    needPayBody: "אפשר להמשיך ממסך ההורה, בלי להתחיל הכול מחדש.",
    needPayCta: "כניסה למסך ההורה",
    prepTitle: "עכשיו נחבר את WhatsApp של {name}",
    prepLead: "צריך לבצע כמה לחיצות פשוטות בטלפון של {name}.",
    whichPhone: "איזה טלפון יש לילד/ה?",
    android: "Android",
    iphone: "iPhone",
    guideAndroidAlt: "מדריך מאויר: איך לקשר את WhatsApp באנדרואיד",
    guideIosAlt: "מדריך מאויר: איך לקשר את WhatsApp ב-iPhone",
    readyCta: "הטלפון מוכן — הציגו לי את הקוד",
    a1: "פתחו את WhatsApp בטלפון של {name}",
    a2: "לחצו על התפריט ⋮",
    a3: "בחרו \"מכשירים מקושרים\"",
    a4: "לחצו \"קישור מכשיר\"",
    i1: "פתחו את WhatsApp בטלפון של {name}",
    i2: "לחצו על הגדרות למטה",
    i3: "בחרו \"מכשירים מקושרים\"",
    i4: "לחצו \"קישור מכשיר\"",
    qrTitle: "סרקו את הקוד",
    qrLead: "בטלפון של {name}, כוונו את המצלמה שמופיעה ב'קישור מכשיר' אל הקוד.",
    qrStay: "אל תסגרו את המסך עד שהחיבור יסתיים.",
    qrSame: "קוד ה-QR צריך להישאר פתוח במסך הזה, ואת הסריקה מבצעים מהטלפון של הילד.",
    waiting: "מחכים לחיבור…",
    waitingSub: "זה בדרך כלל לוקח כמה שניות.",
    preparing: "רגע…",
    failTitle: "לא הצלחנו להשלים את החיבור",
    failBody: "זה קורה לפעמים. אפשר לנסות שוב.",
    retry: "נסו שוב",
    needHelp: "אני צריך עזרה",
    successTitle: "{name} מחובר/ת 🎉",
    successLead: "ChatiSafe פעילה עכשיו.",
    successNote: "אם נזהה שיחה או תוכן שעשויים לדרוש את תשומת לבכם, נוכל להתריע למספר שאיתו נרשמתם.",
    checkChild: "הילד נוסף",
    checkWa: "WhatsApp מחובר",
    checkAlerts: "ההתראות מוכנות",
    continue: "המשך",
    doneTitle: "הכול מוכן 💚",
    doneLead: "ChatiSafe התחילה לשמור על {name}.",
    doneNote: "אם נזהה משהו שעלול לדרוש את תשומת לבכם, נוכל להתריע לכם.",
    doneAccount: "פתחתם חשבון",
    doneChild: "הוספתם את {name}",
    doneWa: "חיברתם את WhatsApp",
    doneActive: "ChatiSafe פעילה",
    enter: "כניסה למסך ההורה",
    addAnother: "הוספת ילד נוסף",
    back: "חזרה",
    qrHow: "איך מתחברים לוואטסאפ של הילד?",
    leave: "שמירה ויציאה",
    sameDevice: "לא ניתן לסרוק ברקוד, שלח קוד במקום",
    sameDeviceLead: "אם אי אפשר לסרוק, נקליד קוד קצר ב-WhatsApp של הילד במקום.",
    codeCta: "הציגו לי קוד להקלדה",
    codeLabel: "מספר הטלפון של הילד",
    codeShown: "הקלידו את הקוד הזה ב-WhatsApp, במסך קישור המכשיר.",
    codeFail: "לא הצלחנו להכין קוד. נסו שוב.",
  },
  auth: {
    welcome: "ברוכים השבים",
    loginLead: "התחברו עם מספר הטלפון של בעל החשבון, או של הורה מאומת בחשבון.",
    email: "אימייל",
    password: "סיסמה",
    confirmPassword: "אימות סיסמה",
    passwordMismatch: "הסיסמאות לא תואמות",
    signingIn: "מתחברים...",
    login: "התחברות",
    newHere: "חדשים כאן?",
    createAccount: "צרו חשבון",
    loginFailed: "ההתחברות נכשלה",
    registerTitle: "יוצרים חשבון",
    registerParent: "הורה",
    registerLead: "אחר כך מוסיפים ילד ומקשרים את הוואטסאפ שלו. הילד הראשון — 3 ימי ניסיון חינם, בלי כרטיס אשראי.",
    fullName: "שם מלא",
    phone: "טלפון",
    creating: "יוצרים...",
    create: "צרו חשבון",
    haveAccount: "כבר יש חשבון?",
    registerFailed: "ההרשמה נכשלה",
    continueRegister: "המשך להרשמה",
    sendingCode: "שולחים קוד...",
    verifyEmailTitle: "אימות אימייל",
    verifyEmailLead: "שלחנו קוד לכתובת",
    verificationCode: "קוד אימות",
    verifying: "מאמתים...",
    confirmEmail: "אימות ויצירת חשבון",
    wrongEmailBack: "לא האימייל הנכון? חזרה לדף ההרשמה",
    emailTaken: "האימייל כבר רשום",
    mailNotConfigured: "שליחת מייל לא מוגדרת בשרת",
    codeSendFailed: "שליחת הקוד נכשלה",
    duplicateSend: "נשלח כבר — המתינו רגע ונסו שוב",
    invalidCode: "קוד שגוי",
    codeExpired: "הקוד פג תוקף — נסו שוב מההתחלה",
    tooManyAttempts: "יותר מדי ניסיונות — נסו שוב מאוחר יותר",
    verificationExpired: "ההרשמה פגה — התחילו מחדש",
    forgotPassword: "שכחתי סיסמה",
    forgotTitle: "איפוס סיסמה",
    forgotLeadEmail: "הזינו האימייל שאיתו נרשמתם. נשלח לשם קוד אימות.",
    sendResetCode: "שליחת קוד",
    forgotCodeLead: "הזינו את הקוד שנשלח לאימייל.",
    noEmailAccess: "אין לי גישה לאימייל שנרשמתי איתו",
    parentPhoneLead: "הזינו מספר טלפון של אחד ההורים הרשומים בחשבון. נשלח קוד בוואטסאפ.",
    parentPhone: "טלפון הורה",
    sendParentCode: "שליחת קוד לוואטסאפ",
    parentCodeLead: "הזינו את הקוד שנשלח לוואטסאפ של ההורה.",
    parentPhoneMismatch: "מספר הטלפון לא תואם להורה בחשבון",
    invalidPhone: "מספר טלפון לא תקין",
    reporterOffline: "שירות ההתראות לא זמין כרגע",
    backToEmailReset: "חזרה לאיפוס באימייל",
    backToLogin: "חזרה להתחברות",
    continue: "המשך",
    resetFailed: "איפוס הסיסמה נכשל",
    newPasswordTitle: "סיסמה חדשה",
    newPasswordLead: "בחרו סיסמה חדשה לחשבון.",
    newPassword: "סיסמה חדשה",
    savingPassword: "שומרים...",
    saveAndLogin: "שמירה והתחברות",
    resetSessionExpired: "פג תוקף האיפוס — התחילו שוב מתהליך שכחתי סיסמה",
    loading: "טוען...",
    registerTitleFull: "יוצרים חשבון הורה",
    emailHint: "נשלח לכאן קוד אימות קצר וקבלות.",
    passwordHint: "לפחות 6 תווים.",
    codeHint: "קוד בן 6 ספרות. בדקו גם בתיקיית הספאם.",
    termsNote: "ביצירת החשבון אתם מאשרים את",
    sideTitle: "שקט נפשי להורים. חוויה רגילה לילד.",
    side1: "ההגנה עובדת מאחורי הקלעים — הילד ממשיך להשתמש בוואטסאפ כרגיל.",
    side2: "כשמשהו מדאיג קורה, אתם מקבלים הודעה ברורה בוואטסאפ עם מה קרה ומה כדאי לעשות.",
    side3: "רק אתם רואים את ההתראות. אין דירוגים, אין פרסומות, אין מכירת מידע.",
    resetStepEmail: "אימייל",
    resetStepCode: "קוד",
    resetStepPassword: "סיסמה חדשה",
    parentPhoneHint: "המספר צריך להיות אחד מנמעני ההתראות המאומתים בחשבון.",
    accountPhone: "טלפון",
    accountPhoneHint: "המספר הזה ישמש לקבלת התראות מ-ChatiSafe. תוכלו להוסיף הורים נוספים בהמשך.",
    notOnWhatsapp: "המספר לא רשום בוואטסאפ ולא יכול לקבל קוד אימות. מספר הטלפון הזה אמור לשמש בתור ההורה הראשי בחשבון.",
    phoneTaken: "המספר כבר רשום כהורה בחשבון אחר.",
    phoneTakenChild: "המספר משויך לילד, ואי אפשר להשתמש בו כהורה.",
    verifyWhatsappTitle: "אימות בוואטסאפ",
    verifyWhatsappLead: "שלחנו קוד לוואטסאפ של",
    wrongPhoneBack: "לא המספר הנכון? חזרה",
    codeHintWhatsapp: "קוד בן 6 ספרות שנשלח לוואטסאפ.",
    loginPhone: "טלפון",
    loginPhoneHint: "מספר הטלפון של בעל החשבון, או של הורה מאומת בחשבון.",
    invalidLogin: "מספר טלפון או סיסמה שגויים",
    forgotLeadPhone: "הזינו מספר טלפון של אחד ההורים המאומתים בחשבון. נשלח קוד בוואטסאפ.",
    phoneAmbiguous: "המספר משויך ליותר מחשבון אחד.",
  },
  billing: {
    tagline: "בלי כאבי ראש. פשוט ₪15 לילד.",
    taglineSub: "בלי תוכניות, בלי מדרגות ובלי אותיות קטנות. כל ילד — ₪15 ל־30 יום. חצי שקל ליום.",
    perChild: "₪15 לילד",
    per30: "ל־30 יום",
    perDay: "₪0.50 ליום",
    trialBadge: "הילד הראשון — 3 ימי ניסיון חינם",
    trialExplain:
      "הילד הראשון שתוסיפו מקבל 3 ימי ניסיון חינם, בלי כרטיס אשראי. בסיום הניסיון ההגנה תושהה עד לתשלום של ₪15 ל־30 יום (₪0.50 ליום).",
    paidExplain:
      "כל ילד נוסף מחויב מיד ב־₪15 ל־30 יום — חצי שקל ליום. בוחרים חידוש אוטומטי או תשלום חד־פעמי.",
    addChildTitle: "הוספת ילד",
    addFirstChildTitle: "הוספת הילד הראשון — ניסיון חינם",
    childNameLabel: "שם הילד (כפי שמופיע בקבוצות)",
    startTrialBtn: "התחילו 3 ימי ניסיון חינם",
    payAndAdd: "תשלום ₪15 והוספת הילד",
    fakeAddBtn: "הוספת הילד",
    fakePayNote: "מצב בדיקה פעיל — לא נדרשים פרטי אשראי. הילד מקבל 30 יום בלי חיוב.",
    payRenew: "תשלום ₪15 ל־30 יום",
    payTitle: "הפעלת הגנה ל־{name}",
    summaryTitle: "סיכום חיוב",
    lineChild: "הגנה ל־{name} · 30 יום",
    lineTotal: "לתשלום היום",
    autoRenewLabel: "חידוש אוטומטי כל 30 יום (₪15)",
    autoRenewHint: "אפשר לכבות בכל רגע. בלי חידוש אוטומטי — ההגנה תושהה בתום 30 הימים עד לתשלום הבא.",
    oneTimeHint: "תשלום חד־פעמי: ההגנה פעילה 30 יום ומסתיימת בלי חיוב נוסף.",
    cardName: "שם על הכרטיס",
    cardNumber: "מספר כרטיס",
    expiry: "תוקף (MM/YY)",
    cvc: "CVC",
    demoNote: "שער תשלום לדוגמה — כל כרטיס תקין עובד חוץ ממספר שמסתיים ב־0000.",
    processing: "מעבדים…",
    cancel: "ביטול",
    stTrial: "ניסיון חינם · נותרו {days} ימים",
    stTrialUnlimited: "ניסיון חינם · ללא תפוגה",
    stTrialEnds: "בסיום הניסיון ({date}) נדרש תשלום של ₪15 ל־30 יום כדי להמשיך",
    stTrialPending: "3 ימי הניסיון נספרים רק מרגע ש-WhatsApp מחובר.",
    stActive: "מוגן עד {date}",
    stActiveDays: "נותרו {days} ימים",
    stAutoOn: "חידוש אוטומטי פעיל — ₪15 יחויבו ב־{date}",
    stAutoOff: "ללא חידוש אוטומטי — ההגנה תושהה ב־{date}",
    stExpired: "ההגנה מושהית — התקופה הסתיימה",
    stExpiredHint: "ההודעות של {name} לא נסרקות ולא נשלחות התראות עד להסדרת התשלום.",
    stNone: "אין תקופת הגנה פעילה",
    payNowBtn: "תשלום ₪15",
    autoOn: "הפעלת חידוש אוטומטי",
    autoOff: "כיבוי חידוש אוטומטי",
    consentLabel:
      "אני מבקש/ת שההגנה תתחיל מיידית, ומאשר/ת שהתקופה (30 יום) משולמת מראש, אינה ניתנת להחזר, ושלא ניתן למחוק את הילד עד סיומה — למעט זכות ביטול על פי חוק. אני מסכים/ה לקבל קבלה כקובץ PDF בוואטסאפ, ואם צוין אימייל — גם לדוא\"ל. קראתי את",
    receiptTitle: "אימייל לקבלה",
    receiptEmailLabel: "אימייל לקבלה",
    receiptEmailInvalid: "האימייל לא תקין",
    receiptUseAccount: "לשלוח לאימייל שנרשמתי איתו",
    receiptOther: "לשלוח גם לאימייל אחר",
    receiptOtherPlaceholder: "name@example.com",
    receiptHint: "לא חובה. הקבלה נשלחת כ־PDF בוואטסאפ לכל ההורים המאומתים. אם תמלאו אימייל, היא תישלח גם לשם — גם בחידוש אוטומטי.",
    receiptLink: "קבלה",
    receiptNoLabel: "קבלה מס׳ {no}",
    consentLink: "תנאי השימוש",
    noRefundNote:
      "מדיניות ללא החזרים: התשלום הוא עבור 30 יום מלאים מרגע האישור. ניתוק וואטסאפ, אי־שימוש או רצון להסיר את הילד אינם מזכים בהחזר. כיבוי חידוש אוטומטי מפסיק חיובים עתידיים בלבד.",
    switchChildHint:
      "רוצים להגן על ילד אחר במקום הזה? לוחצים \"ניתוק\" ומקשרים את הוואטסאפ של הילד האחר עם QR — בלי למחוק ובלי לשלם שוב.",
    errPeriodActive: "יש כבר תקופה משולמת פעילה לילד הזה. אפשר לשלם שוב רק אחרי סיומה.",
    errConsent: "יש לאשר את תנאי התשלום כדי להמשיך.",
    lockTitle: "לא ניתן למחוק את {name} כרגע",
    lockPaid:
      "שילמתם על תקופת הגנה שמסתיימת ב־{date}. התשלום היה מראש ואינו מוחזר, ולכן הילד נשאר בחשבון עד סיום התקופה. אחרי {date} — אם החידוש האוטומטי כבוי — כפתור המחיקה יפעל.",
    lockAutoRenew:
      "החידוש האוטומטי לילד הזה פעיל. כבו אותו קודם, ואז אפשר יהיה למחוק את הילד בתום התקופה המשולמת.",
    lockTurnOffRenew: "כיבוי חידוש אוטומטי",
    lockClose: "הבנתי",
    sectionTitle: "חיוב ותשלומים",
    sectionLead: "₪15 לילד ל־30 יום. חצי שקל ליום. בלי כאבי ראש.",
    activeChildren: "ילדים מוגנים",
    monthlyTotal: "סה״כ ל־30 יום",
    cardOnFile: "כרטיס שמור",
    noCard: "אין כרטיס שמור",
    renewalFailed: "החידוש האחרון נכשל — שלמו ידנית כדי לעדכן כרטיס",
    historyTitle: "היסטוריית תשלומים",
    historyEmpty: "עדיין אין תשלומים",
    hDate: "תאריך",
    hChild: "ילד",
    hDesc: "פירוט",
    hPeriod: "תקופה",
    hAmount: "סכום",
    hStatus: "סטטוס",
    hOrder: "מזהה עסקה",
    kindTrial: "ניסיון חינם (3 ימים)",
    kindPurchase: "הוספת ילד · 30 יום",
    kindRenewal: "חידוש · 30 יום",
    statusOk: "הצליח",
    statusDeclined: "נדחה",
    errCard: "מספר כרטיס לא תקין",
    errExpiry: "תוקף לא תקין (MM/YY)",
    errExpired: "הכרטיס פג תוקף",
    errDeclined: "הכרטיס נדחה (בדמו: כל מספר שלא מסתיים ב־0000)",
    errNoCard: "אין כרטיס שמור — שלמו פעם אחת כדי לשמור כרטיס",
    errTrialUsed: "הניסיון כבר נוצל לאימייל או למספר טלפון של הורה — הוספת ילד כרוכה בתשלום ₪15. ניסיון עצמו לא דורש כרטיס אשראי.",
    trialBlockedNote: "ניסיון חינם כבר הופעל עם האימייל הזה או עם מספר טלפון של הורה בחשבון. ניסיון לא דורש כרטיס אשראי, אבל אי אפשר להתחיל עוד אחד עם אותם פרטים.",
    errPaymentRequired: "נדרש תשלום כדי להפעיל הגנה לילד הזה",
    errGeneric: "משהו השתבש — נסו שוב",
    headerChip: "{n} ילדים מוגנים · ₪15 לילד",
    headerChipNone: "₪15 לילד · הילד הראשון בניסיון חינם, בלי כרטיס",
    attention: "{n} ילדים ממתינים לתשלום — ההגנה עליהם מושהית",
  },
  legal: {
    privacy: "מדיניות פרטיות",
    terms: "תנאי שימוש",
    accessibility: "הצהרת נגישות",
    back: "חזרה",
    updated: "עודכן לאחרונה: {date}",
    toc: "תוכן העניינים",
    print: "הדפסה",
  },
  dash: {
    signedIn: "מחוברים כ־{email}",
    hey: "היי {name}",
    billingUnavailable: "לא ניתן לטעון את מצב החיוב כרגע — נסו לרענן",
    networkUnavailable: "אין חיבור לשרת — בדקו שהאפליקציה רצה ורעננו",
    children: "ילדים",
    childPlaceholder: "שם התצוגה של הילד (כמו בקבוצות)",
    addChild: "הוספת ילד",
    editChildName: "עריכת שם",
    saveChildName: "שמירה",
    renameFail: "עדכון השם נכשל",
    noChildren: "אין עדיין ילדים. הוסיפו את הילד הראשון — 3 ימי ניסיון חינם, בלי כרטיס אשראי — ואז מקשרים וואטסאפ עם QR.",
    on: "ההגנה פעילה",
    linkedPaused: "מקושר · ההגנה מושהית",
    noNumber: "המספר לא זמין — הפעילו הגנה",
    waitingScan: "ממתינים לסריקת וואטסאפ…",
    notLinked: "לא מקושר",
    pause: "השהיה",
    start: "הפעלת הגנה",
    unlink: "ניתוק",
    cancelPair: "ביטול קישור",
    linkWa: "קישור וואטסאפ",
    mockQr: "QR דמו — לא ניתן לסריקה בוואטסאפ אמיתי.",
    qrHint: "וואטסאפ → מכשירים מקושרים → קישור מכשיר",
    waitingQr: "ממתינים ל־QR…",
    mockScan: "פיתוח: סימולציית סריקה",
    orCode: "או קוד קישור",
    phonePh: "טלפון הילד ב־E.164",
    getCode: "קבלת קוד",
    settings: "הגדרות הגנה",
    settingsLead:
      "בחרו על מה לקבל התראות בוואטסאפ. הכול פעיל כברירת מחדל — כבו רק מה שלא מעניין אתכם. השינויים נשמרים אוטומטית.",
    alertLanguage: "שפת הדיווחים",
    alertLanguageDesc: "כל הדיווחים שנשלחים להורים בוואטסאפ ייכתבו בשפה הזו.",
    alertLanguageHe: "עברית",
    alertLanguageEn: "English",
    secBlock: "דיווחי רשימת חסימה",
    secBlockDesc: "התראות כשהילד/ה חוסם/ת או מסיר/ה חסימה מאיש קשר בוואטסאפ.",
    reportBlock: "דיווח על חסימת משתמש",
    reportUnblock: "דיווח על הסרת חסימה ממשתמש",
    secGroups: "דיווחים מקבוצות",
    secGroupsDesc: "אירועי קבוצה והודעות בקבוצה שבהן הילד/ה מוזכר/ת או מצוטט/ת.",
    reportGroupRemoval: "דיווח על הסרה מקבוצה",
    reportGroupJoin: "דיווח על הצטרפות לקבוצה",
    reportGroupHarmful:
      "דיווח על הודעות בעלות תוכן בלתי הולם בקבוצה, כאשר הילד/ה מצוטט/ת או מוזכר/ת בהן",
    secPrivate: "דיווחים מצ'אטים פרטיים",
    secPrivateDesc: "התראות על פעילות ממספרים שאינם שמורים באנשי הקשר של הילד/ה.",
    reportUnknownContact:
      "דיווח על הודעות נכנסות ויוצאות ממספרים שאינם שמורים באנשי הקשר — רק ההודעה הראשונה והשנייה בצ'אט",
    phoneOwnedSame: "מספר הטלפון כבר משויך לילד/ה אחר/ה בחשבון שלך ({name}). ניתקנו את הניסיון לקשר — השתמשו במספר אחר או נתקו את המכשיר הקיים.",
    phoneOwnedOther:
      "מספר הטלפון כבר מקושר לחשבון ChatiSafe אחר. כדי לקשר אותו לילד/ה כאן, פתחו בוואטסאפ של המכשיר → הגדרות → מכשירים מקושרים, והסירו את ChatiSafe מהחשבון האחר. אחר כך נסו לקשר שוב.",
    phoneConflictChildTitle: "המספר כבר משויך לילד/ה",
    phoneConflictParentTitle: "המספר רשום כהורה",
    phoneBlockedChildSame: "המספר הזה כבר משויך לילד/ה {name} בחשבון הזה. אי אפשר לקשר אותו שוב.",
    phoneBlockedChildOther: "המספר הזה כבר מקושר לילד/ה בחשבון אחר. אי אפשר לקשר אותו כאן.",
    phoneBlockedParentSame: "המספר הזה רשום כהורה בחשבון הזה ({name}). אי אפשר לקשר אותו כילד/ה.",
    phoneBlockedParentOther: "המספר הזה רשום כהורה בחשבון אחר. אי אפשר לקשר אותו כילד/ה.",
    phoneOwnedParentSame: "המספר הזה רשום כהורה בחשבון הזה ({name}). ניתקנו את הקישור מיד.",
    phoneOwnedParentOther: "המספר הזה רשום כהורה בחשבון אחר. ניתקנו את הקישור מיד.",
    secGeneral: "דיווחים כלליים",
    secGeneralDesc:
      "סינון לפי סוג מדיה, פרטיות תוכן ההתראה, והודעות יוצאות מהילד/ה. התראות על תוכן מסווג לפי רמת הסיכון.",
    mediaTypes: "סוגי הודעות לדיווח",
    reportMediaImages: "תמונות",
    reportMediaVideos: "סרטונים",
    reportMediaAudio: "הקלטות קול",
    reportMediaText: "הודעות טקסט",
    reportMediaStickers: "סטיקרים",
    privacyMode: "תצוגת תוכן בהתראות",
    privacyShowContent: "הצגת תוכן ההודעות המדווחות",
    privacyHideContent: "שמירת פרטיות הילד/ה — רק סוג ההודעה וסוג התוכן",
    privacyHideHint:
      "במצב פרטיות לא יישלחו ציטוטים, תמונות או הקלטות בהתראה; תקבלו תיאור כללי של סוג האירוע.",
    reportOutgoing: "הצגת דיווחים על הודעות יוצאות שהילד/ה שלח/ה",
    reportOutgoingHint: "כולל תוכן בלתי הולם שהילד/ה שלח/ה לאחרים, לפי סוגי המדיה שסומנו למעלה.",
    classificationGuide:
      "סיווג הודעות: תקין — אין התראה. חשוד — סימנים שכדאי לשים לב (לא בהכרח בלתי הולם). בלתי הולם — תוכן פוגעני או מסוכן שדורש תשומת לב.",
    reportSuspicious: "התראות גם על תוכן חשוד (לא רק בלתי הולם)",
    reportSuspiciousHint:
      "פעיל כברירת מחדל. כשהמערכת לא בטוחה אבל רואה סימנים מדאיגים (למשל שפה דו־משמעית או הקשר חסר), תקבלו התראה עדינה בוואטסאפ — בלי לסמן את ההודעה כבלתי הולמת.",
    secForceBlock: "חסימה קבועה",
    secForceBlockDesc:
      "מספרים שהמערכת חוסמת בחשבון הוואטסאפ של הילד/ה. אם הילד/ה יסיר/ה את החסימה, צ'אטיסייף יחזיר אותה אוטומטית וישלח אליכם התראה נפרדת.",
    secWhitelist: "אנשי קשר בלי דיווחים",
    secWhitelistDesc:
      "מספרים שההורים סומכים עליהם. הודעות מול המספרים האלה לא יישלחו כהתראות להורים.",
    forceBlockAdd: "חסום את המספר",
    whitelistAdd: "הוסף לרשימה",
    contactPhone: "מספר טלפון",
    contactChild: "ילד/ה",
    contactEmpty: "אין מספרים ברשימה",
    contactRemove: "הסרה",
    contactNotConnected: "הילד/ה צריך להיות מחובר/ת לוואטסאפ כדי לבדוק ולעדכן את הרשימה.",
    contactNoChild: "קודם מקשרים ילד/ה.",
    contactSaved: "המספר נוסף",
    addFail: "הוספת הילד נכשלה",
    hideFail: "ההסתרה נכשלה",
    revealFail: "לא הצלחנו להציג ילד שמור",
    startFail: "ההפעלה נכשלה",
    unlinkConfirm: "לנתק וואטסאפ? זה מסיר את המכשיר ממכשירים מקושרים.",
    unlinkFail: "הניתוק נכשל — המכשיר לא הוסר מוואטסאפ",
    deleteLinked: "למחוק את הילד? קודם ינותק הוואטסאפ ממכשירים מקושרים.",
    deletePlain: "למחוק את הילד מצ'אטיסייף?",
    deleteFail: "המחיקה נכשלה",
    pairFail: "הקישור נכשל",
    pairNotOnWa: "המספר הזה לא רשום בוואטסאפ.",
    pairInvalidPhone: "מספר טלפון לא תקין. בדקו קידומת מדינה.",
    pairNeedQr: "עדיין מכינים את הקישור — נסו שוב בעוד רגע.",
    pairPhoneIntl: "הזינו מספר מלא עם קידומת מדינה, לא מספר שמתחיל ב־0.",
    parentsRequired: "קודם מוסיפים ומאמתים הורה. בלי זה ההגנה מושהית.",
    qrFail: "לא הצלחנו להציג קוד QR — נסו לקשר שוב",
    parents: "הורים",
    parentsLead: "הוסיפו הורה שיקבל דיווחים בוואטסאפ על הודעות חשודות. נדרש אימות מספר לפני שמקשרים ילדים.",
    addParent: "הוספת הורה",
    parentName: "שם ההורה",
    parentPhone: "מספר טלפון",
    parentCountry: "מדינה",
    parentSendCode: "שליחת קוד אימות",
    parentVerifyLead: "שלחנו קוד בוואטסאפ. הזינו אותו כאן כדי לאשר את המספר.",
    parentCode: "קוד אימות",
    parentConfirm: "אישור והוספה",
    parentResend: "שליחה מחדש",
    parentRemove: "הסרה",
    parentRemoveConfirm: "להסיר את ההורה מרשימת הדיווחים?",
    parentInvalidPhone: "מספר טלפון לא תקין.",
    parentNotOnWa: "מספר זה לא רשום בוואטסאפ.",
    parentReporterOffline: "שירות הדיווח לא מחובר — נסו שוב מאוחר יותר.",
    parentAlready: "המספר כבר ברשימה.",
    parentTakenParentTitle: "המספר כבר רשום כהורה",
    parentTakenParentHere: "המספר הזה כבר מופיע ברשימת ההורים של החשבון הזה ({name}).",
    parentTakenParentOther: "המספר הזה כבר רשום כהורה בחשבון אחר. אי אפשר להוסיף אותו שוב.",
    parentTakenChildTitle: "המספר כבר רשום כילד",
    parentTakenChildHere: "המספר הזה מקושר לילד/ה {name} בחשבון הזה. אי אפשר להוסיף אותו כהורה.",
    parentTakenChildOther: "המספר הזה כבר מקושר לילד/ה בחשבון אחר. אי אפשר להוסיף אותו כהורה.",
    parentWrongCode: "קוד שגוי.",
    parentCodeExpired: "הקוד פג תוקף — שלחו קוד חדש.",
    parentSendFail: "שליחת הקוד נכשלה.",
    parentGenericFail: "הפעולה נכשלה.",
    parentServerError: "שגיאת שרת — נסו שוב בעוד רגע.",
    parentWaCheckFail: "לא ניתן לבדוק את המספר בוואטסאפ כרגע. נסו שוב בעוד רגע.",
    parentWaServiceOutdated: "שירות ההתראות לא זמין כרגע. נסו שוב מאוחר יותר.",
    parentNoPending: "אין בקשת אימות פעילה — שלחו קוד מחדש.",
    parentTooManyAttempts: "יותר מדי ניסיונות — שלחו קוד חדש.",
    childrenLocked: "הוסיפו ואמתו לפחות הורה אחד כדי לנהל ילדים.",
  },
  common: {
    cancel: "ביטול",
    close: "סגירה",
    save: "שמירה",
    back: "חזרה",
    continue: "המשך",
    confirm: "אישור",
    delete: "מחיקה",
    remove: "הסרה",
    retry: "ניסיון חוזר",
    loading: "טוען…",
    saved: "נשמר",
    saving: "שומרים…",
    optional: "אופציונלי",
    showPassword: "הצגת סיסמה",
    hidePassword: "הסתרת סיסמה",
    moreActions: "פעולות נוספות",
    dismiss: "סגירת ההודעה",
    learnMore: "מידע נוסף",
    yes: "כן",
    no: "לא",
    today: "היום",
    tomorrow: "מחר",
    daysLeft: "נותרו {n} ימים",
    dayLeft: "נותר יום אחד",
    step: "שלב {n}",
    of: "מתוך",
    and: "וגם",
  },
  app: {
    brand: "צ'אטיסייף",
    documentTitle: "צ'אטיסייף — הגנה על וואטסאפ לילדים",
    tabHome: "בית",
    tabParents: "הורים",
    tabParentsShort: "הורים",
    tabParentsHint: "מי מקבל התראות בוואטסאפ",
    tabChildren: "ילדים",
    tabChildrenShort: "ילדים",
    tabChildrenHint: "הטלפונים של הילדים",
    tabRecipients: "הורים",
    tabRecipientsShort: "הורים",
    tabSettings: "הגדרות הגנה",
    tabSettingsShort: "הגנה",
    tabSettingsHint: "מה לבדוק בהודעות",
    tabHistory: "היסטוריה",
    tabHistoryShort: "היסטוריה",
    tabHistoryHint: "התראות שכבר נשלחו",
    tabBilling: "חיוב",
    tabBillingHint: "תשלום והגנה",
    account: "החשבון שלי",
    openAccount: "תפריט חשבון",
    signedInAs: "מחוברים כ־",
    navLabel: "ניווט ראשי",
    historyLead: "כל ההתראות שנשלחו להורים. החדשות למעלה.",
  },
  home: {
    greeting: "שלום, {name}",
    statusAllOk: "כל הילדים מוגנים",
    statusAllOkSub: "ההגנה פעילה ל־{n} ילדים. אם יקרה משהו מדאיג — תקבלו הודעה בוואטסאפ.",
    statusOneOk: "{name} מוגן/ת",
    statusOneOkSub: "ההגנה פעילה. אם יקרה משהו מדאיג — תקבלו הודעה בוואטסאפ.",
    statusAttention: "{n} ילדים דורשים את תשומת לבכם",
    statusAttentionOne: "ילד אחד דורש את תשומת לבכם",
    statusAttentionSub: "טפלו בפריטים למטה כדי שההגנה תחזור לפעול.",
    statusNone: "ההגנה עדיין לא פעילה",
    statusNoneSub: "השלימו את שלבי ההתחלה כדי להגן על הילד הראשון.",
    statusPaused: "ההגנה מושהית",
    statusPausedSub: "ההגנה מושהית לכל הילדים. הפעילו אותה מחדש כשתרצו.",
    attentionTitle: "דורש טיפול",
    childrenTitle: "הילדים שלי",
    childrenLead: "אנחנו עוקבים אחר הודעות ותכנים שעלולים לדרוש את תשומת לבכם.",
    protectOn: "ChatiSafe פעילה",
    protectWa: "WhatsApp מחובר",
    protectOff: "ה-WhatsApp אינו מחובר",
    protectNeed: "נדרשת פעולה",
    connectTitle: "צריך לחבר את WhatsApp של {name}",
    reconnectTitle: "ה-WhatsApp של {name} נותק",
    reconnectBody: "כדי ש-ChatiSafe תמשיך לפעול, צריך לחבר אותו מחדש.",
    reconnectBtn: "חיבור מחדש",
    emptyConnectTitle: "אין עדיין ילד מחובר",
    emptyConnectBody: "נוסיף עכשיו את הילד הראשון. זה לוקח רק כמה דקות.",
    emptyConnectAction: "הוספת הילד הראשון",
    checklistTitle: "השלימו את ההגדרה",
    checkAccount: "פתחתם חשבון",
    checkChild: "הוספתם את {name}",
    checkChildPending: "הוסיפו ילד",
    checkWa: "חברו את WhatsApp",
    checkDone: "סיימו את ההגדרה",
    resumeBtn: "המשך מהמקום שעצרתי",
    welcomeBanner: "🎉 ChatiSafe פעילה עבור {name}",
    dismiss: "סגירה",
    shareTitle: "רוצים לשתף הורה נוסף?",
    shareBody: "הוסיפו בן/בת זוג או הורה נוסף לקבלת התראות.",
    shareBtn: "הוספת הורה נוסף",
    setupTitle: "שלושה שלבים והילד מוגן",
    setupLead: "כל שלב לוקח כדקה. אפשר לעצור ולהמשיך בכל רגע.",
    setup1: "הוסיפו מספר וואטסאפ לקבלת התראות",
    setup1Sub: "לשם נשלחות ההתראות. נאמת אותו עם קוד קצר.",
    setup2: "הוסיפו את הילד הראשון",
    setup2Sub: "3 ימי ניסיון חינם, בלי כרטיס אשראי.",
    setup3: "קשרו את הוואטסאפ של הילד",
    setup3Sub: "סורקים QR פעם אחת מהטלפון של הילד.",
    setupDone: "הושלם",
    setupStart: "התחלה",
    howAlertsTitle: "איך תקבלו התראות?",
    howAlertsBody:
      "כשמזוהה תוכן מסוכן — בריונות, איומים, זרים או מדיה לא הולמת — נשלחת הודעת וואטסאפ לנמעני ההתראות שהגדרתם. הילד לא רואה דבר.",
    recipientsCount: "{n} נמענים",
    recipientsOne: "נמען אחד",
    recipientsNone: "עוד לא הוגדרו נמענים",
    manageRecipients: "ניהול נמענים",
    actNeedsPayment: "ההגנה על {name} מושהית — נדרש תשלום",
    actNotLinked: "{name} עדיין לא מקושר/ת לוואטסאפ",
    actPaused: "ההגנה על {name} מושהית",
    actPairing: "ממתינים לסריקת QR של {name}",
    actTrialEnding: "הניסיון של {name} מסתיים {when}",
    actRenewalFailed: "החידוש של {name} נכשל — עדכנו כרטיס",
    actNoRecipients: "אין מספר לקבלת התראות — ההתראות לא יישלחו",
    actNoPhone: "המספר של {name} עוד לא זוהה",
    payNow: "לתשלום",
    linkNow: "קישור",
    resume: "הפעלה מחדש",
    continueLink: "המשך קישור",
    addRecipient: "הוספת מספר",
    refresh: "רענון",
    recentTitle: "מה קרה לאחרונה",
    recentEmpty: "אין התראות עדיין",
    recentEmptySub: "כשמזוהה תוכן מדאיג — הוא יופיע כאן וגם יישלח בוואטסאפ.",
    recentEmptyProtected: "ההגנה פעילה. כשמזוהה תוכן מדאיג — הוא יופיע כאן.",
    recentHidden: "תוכן ההודעה מוסתר לפי הגדרות הפרטיות.",
    incoming: "התקבלה",
    outgoing: "נשלחה",
    sevUrgent: "דחוף",
    sevSuspicious: "חשוד",
    sevWarn: "אזהרה",
    sevWatch: "למעקב",
    sevLow: "נמוך",
    cats: {
      profanity: "שפה גסה",
      insult: "השפלה",
      harassment: "הטרדה",
      bullying: "בריונות",
      threat: "איום",
      violence: "אלימות",
      hate: "שנאה",
      sexual: "תוכן מיני",
      sexual_harassment: "הטרדה מינית",
      sexual_exploitation: "ניצול מיני",
      grooming: "גרומינג",
      coercion: "כפייה",
      manipulation: "מניפולציה",
      humiliation: "השפלה",
      self_harm: "פגיעה עצמית",
      privacy: "פרטיות",
      dangerous: "תוכן מסוכן",
      extremism: "קיצוניות",
    },
  },
  child: {
    stProtected: "מוגן/ת",
    stPaused: "מושהה",
    stNotLinked: "לא מקושר",
    stPairing: "בהמתנה לסריקה",
    stNeedsPayment: "נדרש תשלום",
    stTrial: "ניסיון חינם",
    trialLine: "ניסיון חינם · {days}",
    trialUnlimited: "ניסיון חינם · ללא תפוגה",
    activeLine: "מוגן עד {date}",
    autoOnLine: "חידוש אוטומטי ב־{date}",
    autoOffLine: "ללא חידוש · ההגנה תושהה ב־{date}",
    expiredLine: "התקופה הסתיימה — ההודעות לא נסרקות",
    issuePayment: "ההגנה מושהית — נדרש תשלום",
    issueRenewal: "החידוש נכשל — עדכנו את הכרטיס",
    issuePaused: "ההגנה מושהית",
    issueNotLinked: "עדיין לא מקושר/ת לוואטסאפ",
    issuePairing: "ממתינים לסריקת QR",
    issueTrialEnding: "הניסיון מסתיים {when}",
    noneLine: "אין תקופת הגנה פעילה",
    phoneUnknown: "המספר יזוהה אחרי ההפעלה",
    actLink: "קישור וואטסאפ",
    actContinueLink: "המשך קישור",
    actResume: "הפעלת הגנה",
    actPause: "השהיה",
    actPay: "תשלום ₪15",
    actRename: "שינוי שם",
    actUnavailable: "הפוך ללא זמין",
    actUnlink: "ניתוק וואטסאפ",
    actDelete: "מחיקת הילד",
    actCancelPair: "ביטול קישור",
    deleteLockedHint: "לא ניתן למחוק עד סוף התקופה המשולמת",
    renameTitle: "שינוי שם",
    renameLabel: "שם הילד",
    renameHint: "השם שמופיע בקבוצות, כדי שנזהה אזכורים.",
    unlinkTitle: "לנתק את הוואטסאפ של {name}?",
    unlinkBody: "המכשיר יוסר מ״מכשירים מקושרים״ בוואטסאפ של הילד. ההגנה תיפסק עד לקישור מחדש. התקופה המשולמת נשמרת.",
    unlinkConfirm: "ניתוק",
    deleteTitle: "למחוק את {name}?",
    deleteBodyLinked: "הוואטסאפ ינותק קודם, ואז הילד יימחק מהחשבון. אי אפשר לבטל את הפעולה.",
    deleteBodyPlain: "הילד יימחק מהחשבון. אי אפשר לבטל את הפעולה.",
    deleteConfirm: "מחיקה",
    addChild: "הוספת ילד",
    addNeedsLinkTitle: "אי אפשר להוסיף ילד",
    addNeedsLink: "יש ילד שעדיין לא מקושר לוואטסאפ. קשרו אותו לפני הוספת ילד נוסף.",
    emptyTitle: "עוד אין ילדים",
    emptyBody: "הוסיפו את הילד הראשון — 3 ימי ניסיון חינם, בלי כרטיס אשראי — ואז קשרו את הוואטסאפ שלו בסריקת QR.",
    lockedTitle: "קודם מוסיפים הורה",
    lockedBody: "בלי הורה מאומת אין הגנה ואין דיווחים. הוסיפו את המספר שלכם בעמוד ההורים.",
    lockedAction: "לעמוד ההורים",
  },
  link: {
    title: "קישור הוואטסאפ של {name}",
    lead: "מהטלפון של הילד:",
    step1: "פותחים וואטסאפ ← ⋮ או הגדרות",
    step2: "מכשירים מקושרים ← קישור מכשיר",
    step3: "סורקים את הקוד שמופיע פה",
    waitingQr: "מכינים קוד QR…",
    qrAlt: "קוד QR לקישור וואטסאפ",
    qrExpiresHint: "הקוד מתחדש אוטומטית. אין צורך לרענן.",
    codeToggle: "לא יכולים לסרוק? קישור עם קוד",
    codeLead: "מקלידים את מספר הוואטסאפ של הילד. אם המספר קיים — יופיע כאן קוד להקלדה בטלפון שלו (לא נשלח ב-SMS).",
    phoneLabel: "מספר הוואטסאפ של הילד",
    phonePh: "050-000-0000",
    getCode: "הצגת קוד קישור",
    checkingWa: "בודקים אם המספר בוואטסאפ…",
    codeReady: "בטלפון של הילד: מכשירים מקושרים ← קישור עם מספר טלפון ← מקלידים את הקוד הזה:",
    connectedTitle: "{name} מקושר/ת!",
    connectedBody: "ההגנה פעילה. אפשר לסגור את החלון.",
    cancel: "ביטול הקישור",
    mockNote: "מצב פיתוח — QR לדוגמה, לא ניתן לסריקה.",
    mockScan: "סימולציית סריקה",
  },
  recipients: {
    title: "הורים",
    lead: "ההורים שיופיעו כאן מקבלים התראות בוואטסאפ.",
    add: "הוספת הורה",
    empty: "עוד אין הורים",
    emptyBody: "הוסיפו את המספר שלכם (או של בן/בת הזוג). רק אחרי האימות אפשר להוסיף ילדים ולהפעיל הגנה.",
    addTitle: "הוספת הורה",
    addLead: "נשלח קוד אימות קצר בוואטסאפ למספר הזה.",
    verifyTitle: "אימות המספר",
    verifyLead: "שלחנו קוד בוואטסאפ ל־{phone}. הזינו אותו כאן.",
    nameLabel: "שם",
    namePh: "למשל: אמא, אבא, דנה",
    phoneLabel: "מספר וואטסאפ",
    sendCode: "שליחת קוד",
    confirm: "אישור והוספה",
    resend: "שליחת קוד מחדש",
    changeNumber: "שינוי מספר",
    removeTitle: "להסיר את {name}?",
    removeBody: "המספר {phone} יפסיק לקבל התראות.",
    removeConfirm: "הסרה",
    verified: "מאומת",
  },
  errors: {
    notFoundTitle: "הדף לא נמצא",
    notFoundBody: "הקישור שהגעתם דרכו לא קיים או הועבר.",
    errorTitle: "משהו השתבש",
    errorBody: "אירעה שגיאה בלתי צפויה. נסו לרענן — ואם זה חוזר, כתבו לנו.",
    goHome: "לדף הבית",
    goDashboard: "ללוח הבקרה",
    tryAgain: "ניסיון חוזר",
  },
  admin: {
    title: "אדמין",
    password: "סיסמת אדמין",
    enter: "כניסה",
    wrong: "סיסמה שגויה",
    users: "משתמשים",
    payments: "תשלומים",
    sessions: "סשנים",
    logout: "יציאה",
    all: "הכל",
    paying: "משלמים",
    notPaying: "לא משלמים",
    noUsers: "אין משתמשים",
    noSessions: "אין סשנים",
    noPayments: "אין משלמים",
    name: "שם",
    email: "אימייל",
    phone: "טלפון",
    plan: "תוכנית",
    none: "אין",
    child: "ילד",
    session: "סשן",
    waPhone: "מספר מחובר",
    notConnected: "לא מחובר",
    running: "רץ",
    stopped: "לא רץ",
    connected: "מחובר",
    owner: "משתמש",
    registered: "נרשם",
    expires: "תפוגה",
    count: "{n} משתמשים",
  },
};

const en = {
  nav: {
    how: "How it works",
    protection: "Protection",
    plans: "Pricing",
    login: "Log in",
    logout: "Log out",
    startFree: "Start here",
    dashboard: "Dashboard",
    mainNav: "Main navigation",
    themeLight: "Light mode",
    themeDark: "Dark mode",
    language: "Language",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  footer: {
    tagline: "WhatsApp protection built for families. ₪15 per child — no headaches.",
    privacy: "Privacy",
    terms: "Terms",
    accessibility: "Accessibility statement",
    links: "Links",
  },
  cookieBanner: {
    title: "Cookie notice",
    message:
      "This site uses essential cookies and local storage to operate (login, language, accessibility and preferences). By continuing to use the site you agree to this.",
    privacyLink: "Privacy Policy",
    accept: "OK",
  },
  a11y: {
    button: "Accessibility",
    title: "Accessibility menu",
    close: "Close",
    fontSize: "Text size",
    increase: "Increase",
    decrease: "Decrease",
    contrast: "High contrast",
    grayscale: "Grayscale",
    links: "Highlight links",
    readable: "Readable font",
    spacing: "Text spacing",
    motion: "Stop animations",
    cursor: "Large cursor",
    focus: "Keyboard focus highlight",
    reset: "Reset settings",
    statement: "Accessibility statement",
    skip: "Skip to main content",
  },
  landing: {
    h1: "Want to know what's happening on your child's WhatsApp?",
    lead: "ChatiSafe helps spot conversations and content that may need your attention.",
    cta: "Start here",
    haveAccount: "I already have an account",
    freeNow: "Free for now",
    seeHow: "See how it works",
    setup: "Setup in 60 seconds · Secure WhatsApp linking · Privacy-first scanning",
    reality: "The reality",
    realityH2: "WhatsApp wasn't built for kids.",
    realityP: "Your child needs it to stay connected. You need sleep. Those two things collide every night.",
    pain1t: "Bullying & exclusion",
    pain1b: "Silent group removals and mean messages leave marks parents never see in time.",
    pain2t: "Threats & strangers",
    pain2b: "Unknown numbers and pressure tactics show up long before a kid knows how to react.",
    pain3t: "Inappropriate media",
    pain3b: "Images and videos arrive without warning — and disappear before anyone asks.",
    how: "How it works",
    howH2: "Set it once. Stay protected.",
    step1t: "Create your parent account",
    step1b: "Register and add your first child — 3-day free trial, no card needed.",
    step2t: "Link your child's WhatsApp",
    step2b: "Scan a QR code from your child's phone, just like linking WhatsApp to a computer. Under a minute.",
    step3t: "Protection works quietly",
    step3b: "Incoming messages are checked. When something's worrying, you get an alert. Your child keeps chatting as usual.",
    prot: "Protection",
    protH2: "What we check, and what you get.",
    f1t: "Bullying, threats and pressure",
    f1b: "When hurtful language, a threat or social pressure shows up, you get a clear WhatsApp alert.",
    f2t: "Privacy first",
    f2b: "We only check what reaches your child. You can choose alerts without the message text itself.",
    f3t: "₪15 per child. That's it.",
    f3b: "No plans, no tiers — every child is ₪15 for 30 days, all managed from one dashboard.",
    f4t: "Exclusion from groups",
    f4b: "Being removed from a group or ignored — hard to see from the outside, and it says a lot.",
    f5t: "Strangers and unknown numbers",
    f5b: "An alert when someone not in your child's contacts reaches out for the first time.",
    f6t: "You decide what matters",
    f6b: "Choose what to be alerted about: images, videos, voice notes, blocks and more — in simple settings.",
    ctaH2: "Protect your child in minutes.",
    ctaP: "First child free for 3 days, no credit card. Then ₪15 per child for 30 days — half a shekel a day. Sleep a little better tonight.",
    startTrial: "Start free trial",
    viewPlans: "See pricing",
    pricing: "Pricing",
    pricingH2: "No headaches. Just ₪15 per child.",
    pricingP:
      "No plans, no tiers, no fine print. Every child — ₪15 for 30 days. That's half a shekel a day for peace of mind.",
    pricingPrice: "₪15",
    pricingUnit: "per child · per 30 days",
    pricingPerDay: "= ₪0.50 a day",
    pricingPoints: [
      "First child — 3-day free trial, no credit card",
      "Every additional child — ₪15 for 30 days, from day one",
      "Auto-renew or one-time payment — your call",
      "No commitment: turn auto-renew off anytime. Each 30-day period is prepaid and non-refundable",
    ],
    pricingCta: "Start with a free trial",
  },
  hero: {
    linked: "Dana's WhatsApp",
    protected: "Protected",
    group: "Class group",
    groupD: "Exclusion pattern noticed — alert sent",
    unknown: "Unknown number",
    unknownD: "First message from a stranger",
    media: "Image received",
    mediaD: "Checked · nothing concerning",
    session: "Protection",
    live: "Active · checking incoming messages",
    parent: "Alert to you",
    parentD: "Hurtful language in the class group. Details and what to do were sent to your WhatsApp.",
    child: "On your child's phone",
    childD: "Normal WhatsApp. Nothing changes.",
  },
  onboarding: {
    help: "Need help?",
    helpTitle: "Need help?",
    helpBody: "You can go back one step, or leave and come back later. We'll continue from where you stopped. No technical knowledge needed.",
    stepOf: "Step {step} of {total}",
    welcomeTitle: "Let's look after your child 💚",
    welcomeLead: "We'll walk you through a few simple steps. It only takes a few minutes.",
    welcomeCta: "Let's start",
    noTech: "No technical knowledge needed.",
    resumeTitle: "Hi, we just need to finish connecting {name}.",
    resumeCta: "Continue",
    childTitle: "Who are we looking after?",
    childLead: "Add the first child you'd like to connect to ChatiSafe.",
    childName: "Child's name",
    childNameError: "Enter a name for your child",
    childCta: "Continue to WhatsApp",
    adding: "Adding {name}…",
    addFail: "We couldn't add them. Try again.",
    needPayTitle: "To add another child, continue from the parent screen",
    needPayBody: "You can continue from the parent screen without starting over.",
    needPayCta: "Go to the parent screen",
    prepTitle: "Now we'll connect {name}'s WhatsApp",
    prepLead: "A few simple taps on {name}'s phone.",
    whichPhone: "Which phone does your child have?",
    android: "Android",
    iphone: "iPhone",
    guideAndroidAlt: "Illustrated guide: how to link WhatsApp on Android",
    guideIosAlt: "Illustrated guide: how to link WhatsApp on iPhone",
    readyCta: "The phone is ready — show me the code",
    a1: "Open WhatsApp on {name}'s phone",
    a2: "Tap the ⋮ menu",
    a3: "Choose \"Linked devices\"",
    a4: "Tap \"Link a device\"",
    i1: "Open WhatsApp on {name}'s phone",
    i2: "Tap Settings at the bottom",
    i3: "Choose \"Linked devices\"",
    i4: "Tap \"Link a device\"",
    qrTitle: "Scan the code",
    qrLead: "On {name}'s phone, point the camera from \"Link a device\" at this code.",
    qrStay: "Don't close this screen until the connection finishes.",
    qrSame: "Leave this QR code open on this screen. The scan is done from your child's phone.",
    waiting: "Waiting for the connection…",
    waitingSub: "This usually takes a few seconds.",
    preparing: "One moment…",
    failTitle: "We couldn't finish the connection",
    failBody: "This happens sometimes. You can try again.",
    retry: "Try again",
    needHelp: "I need help",
    successTitle: "{name} is connected 🎉",
    successLead: "ChatiSafe is active now.",
    successNote: "If we notice a conversation or content that may need your attention, we can alert the number you signed up with.",
    checkChild: "Child added",
    checkWa: "WhatsApp connected",
    checkAlerts: "Alerts are ready",
    continue: "Continue",
    doneTitle: "You're all set 💚",
    doneLead: "ChatiSafe has started looking after {name}.",
    doneNote: "If we notice something that may need your attention, we can alert you.",
    doneAccount: "You opened an account",
    doneChild: "You added {name}",
    doneWa: "You connected WhatsApp",
    doneActive: "ChatiSafe is active",
    enter: "Go to the parent screen",
    addAnother: "Add another child",
    back: "Back",
    qrHow: "How do I connect my child's WhatsApp?",
    leave: "Save and leave",
    sameDevice: "Can't scan the code, send a code instead",
    sameDeviceLead: "If you can't scan, we'll show a short code to type into your child's WhatsApp instead.",
    codeCta: "Show me a code to type",
    codeLabel: "Your child's phone number",
    codeShown: "Type this code in WhatsApp, on the link-a-device screen.",
    codeFail: "We couldn't prepare a code. Try again.",
  },
  auth: {
    welcome: "Welcome back",
    loginLead: "Log in with the account phone, or a verified parent's phone.",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    passwordMismatch: "Passwords do not match",
    signingIn: "Signing in...",
    login: "Log in",
    newHere: "New here?",
    createAccount: "Create an account",
    loginFailed: "Login failed",
    registerTitle: "Create your",
    registerParent: "parent",
    registerLead: "Then add a child and link their WhatsApp. First child — 3-day free trial, no credit card.",
    fullName: "Full name",
    phone: "Phone",
    creating: "Creating...",
    create: "Create account",
    haveAccount: "Already have an account?",
    registerFailed: "Registration failed",
    continueRegister: "Continue",
    sendingCode: "Sending code...",
    verifyEmailTitle: "Verify your email",
    verifyEmailLead: "We sent a code to",
    verificationCode: "Verification code",
    verifying: "Verifying...",
    confirmEmail: "Verify and create account",
    wrongEmailBack: "Wrong email? Back to registration",
    emailTaken: "Email already registered",
    mailNotConfigured: "Email is not configured on the server",
    codeSendFailed: "Could not send code",
    duplicateSend: "Already sent — wait a moment and try again",
    invalidCode: "Invalid code",
    codeExpired: "Code expired — start again",
    tooManyAttempts: "Too many attempts — try again later",
    verificationExpired: "Registration expired — start over",
    forgotPassword: "Forgot password",
    forgotTitle: "Reset password",
    forgotLeadEmail: "Enter the email you registered with. We'll send a verification code.",
    sendResetCode: "Send code",
    forgotCodeLead: "Enter the code we sent to your email.",
    noEmailAccess: "I don't have access to my registration email",
    parentPhoneLead: "Enter a parent phone number on your account. We'll send a WhatsApp code.",
    parentPhone: "Parent phone",
    sendParentCode: "Send WhatsApp code",
    parentCodeLead: "Enter the code sent to the parent's WhatsApp.",
    parentPhoneMismatch: "That phone doesn't match a parent on the account",
    invalidPhone: "Invalid phone number",
    reporterOffline: "Alert service is offline",
    backToEmailReset: "Back to email reset",
    backToLogin: "Back to log in",
    continue: "Continue",
    resetFailed: "Password reset failed",
    newPasswordTitle: "New password",
    newPasswordLead: "Choose a new password for your account.",
    newPassword: "New password",
    savingPassword: "Saving...",
    saveAndLogin: "Save and log in",
    resetSessionExpired: "Reset session expired — start forgot password again",
    loading: "Loading...",
    registerTitleFull: "Create your parent account",
    emailHint: "We'll send a short verification code and receipts here.",
    passwordHint: "At least 6 characters.",
    codeHint: "6-digit code. Check your spam folder too.",
    termsNote: "By creating an account you agree to the",
    sideTitle: "Peace of mind for parents. A normal chat for your child.",
    side1: "Protection runs in the background — your child keeps using WhatsApp as usual.",
    side2: "When something worrying happens, you get a clear WhatsApp message with what happened and what to do.",
    side3: "Only you see the alerts. No ratings, no ads, no selling data.",
    resetStepEmail: "Email",
    resetStepCode: "Code",
    resetStepPassword: "New password",
    parentPhoneHint: "Must be one of the verified alert recipients on the account.",
    accountPhone: "Phone",
    accountPhoneHint: "This number will receive alerts from ChatiSafe. You can add more parents later.",
    notOnWhatsapp: "This number isn't on WhatsApp, so it can't receive a verification code. This phone is meant to be the primary parent on the account.",
    phoneTaken: "This number is already a parent on another account.",
    phoneTakenChild: "This number belongs to a child and can't be used as a parent.",
    verifyWhatsappTitle: "Verify on WhatsApp",
    verifyWhatsappLead: "We sent a code to",
    wrongPhoneBack: "Wrong number? Go back",
    codeHintWhatsapp: "6-digit code sent on WhatsApp.",
    loginPhone: "Phone",
    loginPhoneHint: "The account owner's phone, or a verified parent's phone.",
    invalidLogin: "Wrong phone or password",
    forgotLeadPhone: "Enter a verified parent's phone. We'll send a WhatsApp code.",
    phoneAmbiguous: "This number is linked to more than one account.",
  },
  billing: {
    tagline: "No headaches. Just ₪15 per child.",
    taglineSub: "No plans, no tiers, no fine print. Every child — ₪15 for 30 days. Half a shekel a day.",
    perChild: "₪15 per child",
    per30: "per 30 days",
    perDay: "₪0.50 / day",
    trialBadge: "First child — 3-day free trial",
    trialExplain:
      "The first child you add gets a 3-day free trial, no card needed. When the trial ends, protection pauses until you pay ₪15 for 30 days (₪0.50/day).",
    paidExplain:
      "Every additional child is charged ₪15 for 30 days right away — half a shekel a day. Choose auto-renew or a one-time payment.",
    addChildTitle: "Add a child",
    addFirstChildTitle: "Add your first child — free trial",
    childNameLabel: "Child's name (as shown in groups)",
    startTrialBtn: "Start 3-day free trial",
    payAndAdd: "Pay ₪15 & add child",
    fakeAddBtn: "Add child",
    fakePayNote: "Test mode is on — no card details. The child gets 30 days with no charge.",
    payRenew: "Pay ₪15 for 30 days",
    payTitle: "Activate protection for {name}",
    summaryTitle: "Billing summary",
    lineChild: "Protection for {name} · 30 days",
    lineTotal: "Due today",
    autoRenewLabel: "Auto-renew every 30 days (₪15)",
    autoRenewHint: "Turn it off anytime. Without auto-renew, protection pauses after 30 days until the next payment.",
    oneTimeHint: "One-time payment: protection runs for 30 days and ends with no further charge.",
    cardName: "Name on card",
    cardNumber: "Card number",
    expiry: "Expiry (MM/YY)",
    cvc: "CVC",
    demoNote: "Demo gateway — any valid card works except numbers ending in 0000.",
    processing: "Processing…",
    cancel: "Cancel",
    stTrial: "Free trial · {days} days left",
    stTrialUnlimited: "Free trial · no end date",
    stTrialEnds: "When the trial ends ({date}) you'll need to pay ₪15 for 30 days to continue",
    stTrialPending: "The 3 trial days start only once WhatsApp is connected.",
    stActive: "Protected until {date}",
    stActiveDays: "{days} days left",
    stAutoOn: "Auto-renew on — ₪15 will be charged on {date}",
    stAutoOff: "No auto-renew — protection pauses on {date}",
    stExpired: "Protection paused — period ended",
    stExpiredHint: "{name}'s messages aren't scanned and no alerts are sent until payment is made.",
    stNone: "No active protection period",
    payNowBtn: "Pay ₪15",
    autoOn: "Turn on auto-renew",
    autoOff: "Turn off auto-renew",
    consentLabel:
      "I ask for protection to start immediately and acknowledge that the 30-day period is prepaid, non-refundable, and that the child cannot be deleted before it ends — except where the law grants a right of cancellation. I agree to receive the receipt as a PDF on WhatsApp, and by email if an address was given. I have read the",
    receiptTitle: "Receipt email",
    receiptEmailLabel: "Receipt email",
    receiptEmailInvalid: "That email isn't valid",
    receiptUseAccount: "Send to the email I registered with",
    receiptOther: "Also send to another email",
    receiptOtherPlaceholder: "name@example.com",
    receiptHint: "Optional. The receipt is sent as a PDF on WhatsApp to every verified parent. If you enter an email, it goes there too — including on auto-renewal.",
    receiptLink: "Receipt",
    receiptNoLabel: "Receipt No. {no}",
    consentLink: "Terms of Service",
    noRefundNote:
      "No-refund policy: you are paying for a full 30 days starting now. Unlinking WhatsApp, not using the Service or wanting to remove the child does not entitle you to a refund. Turning auto-renew off only stops future charges.",
    switchChildHint:
      "Want to protect a different child in this slot? Press \"Unlink\" and pair the other child's WhatsApp with a QR — no deletion, no second payment.",
    errPeriodActive: "This child already has a paid period running. You can pay again only after it ends.",
    errConsent: "Please accept the payment terms to continue.",
    lockTitle: "{name} can't be deleted right now",
    lockPaid:
      "You paid for a protection period ending on {date}. The payment was made in advance and is non-refundable, so the child stays in your account until then. After {date} — with auto-renew off — the delete button will work.",
    lockAutoRenew:
      "Auto-renew is on for this child. Turn it off first; deletion becomes possible when the paid period ends.",
    lockTurnOffRenew: "Turn off auto-renew",
    lockClose: "Got it",
    sectionTitle: "Billing & payments",
    sectionLead: "₪15 per child for 30 days. Half a shekel a day. No headaches.",
    activeChildren: "Protected children",
    monthlyTotal: "Total per 30 days",
    cardOnFile: "Card on file",
    noCard: "No card on file",
    renewalFailed: "Last renewal failed — pay manually to update your card",
    historyTitle: "Payment history",
    historyEmpty: "No payments yet",
    hDate: "Date",
    hChild: "Child",
    hDesc: "Details",
    hPeriod: "Period",
    hAmount: "Amount",
    hStatus: "Status",
    hOrder: "Order ID",
    kindTrial: "Free trial (3 days)",
    kindPurchase: "Child added · 30 days",
    kindRenewal: "Renewal · 30 days",
    statusOk: "Succeeded",
    statusDeclined: "Declined",
    errCard: "Invalid card number",
    errExpiry: "Invalid expiry (MM/YY)",
    errExpired: "Card has expired",
    errDeclined: "Card declined (demo: any number not ending in 0000)",
    errNoCard: "No card on file — pay once to save a card",
    errTrialUsed: "This email or a parent phone already used the free trial — adding a child costs ₪15. The trial itself does not require a card.",
    trialBlockedNote: "A free trial already started with this email or with a parent phone on this account. A trial does not require a card, but the same details cannot start another one.",
    errPaymentRequired: "Payment is required to activate protection for this child",
    errGeneric: "Something went wrong — try again",
    headerChip: "{n} protected children · ₪15 per child",
    headerChipNone: "₪15 per child · first child free for 3 days, no card",
    attention: "{n} children awaiting payment — their protection is paused",
  },
  legal: {
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    accessibility: "Accessibility Statement",
    back: "Back",
    updated: "Last updated: {date}",
    toc: "Contents",
    print: "Print",
  },
  dash: {
    signedIn: "Signed in as {email}",
    hey: "Hey {name}",
    billingUnavailable: "Could not load your billing status — try refreshing",
    networkUnavailable: "Cannot reach the server — check that the app is running and refresh",
    children: "Children",
    childPlaceholder: "Child display name (as in groups)",
    addChild: "Add child",
    editChildName: "Edit name",
    saveChildName: "Save",
    renameFail: "Could not update name",
    noChildren: "No children yet. Add your first child — 3-day free trial, no credit card — then link WhatsApp with QR.",
    on: "Protection on",
    linkedPaused: "Linked · protection paused",
    noNumber: "Number unavailable — Start protection",
    waitingScan: "Waiting for WhatsApp scan…",
    notLinked: "Not linked",
    pause: "Pause",
    start: "Start protection",
    unlink: "Unlink",
    cancelPair: "Cancel pairing",
    linkWa: "Link WhatsApp",
    mockQr: "MOCK QR — not scannable by real WhatsApp.",
    qrHint: "WhatsApp → Linked Devices → Link a Device",
    waitingQr: "Waiting for QR…",
    mockScan: "Dev: simulate scan",
    orCode: "Or use pairing code",
    phonePh: "Child phone E.164",
    getCode: "Get code",
    settings: "Protection settings",
    settingsLead:
      "Choose what you get WhatsApp alerts about. Everything is on by default — turn off only what you don't need. Changes save automatically.",
    alertLanguage: "Report language",
    alertLanguageDesc: "Every report sent to parents on WhatsApp is written in this language.",
    alertLanguageHe: "עברית",
    alertLanguageEn: "English",
    secBlock: "Block list reports",
    secBlockDesc: "Alerts when your child blocks or unblocks a WhatsApp contact.",
    reportBlock: "Report when a user is blocked",
    reportUnblock: "Report when a block is removed",
    secGroups: "Reports from groups",
    secGroupsDesc: "Group membership events and harmful group messages that quote or mention your child.",
    reportGroupRemoval: "Report removal from a group",
    reportGroupJoin: "Report joining a group",
    reportGroupHarmful:
      "Report harmful group messages when your child is quoted or mentioned",
    secPrivate: "Private chat reports",
    secPrivateDesc: "Alerts for activity from numbers not saved in your child's contacts.",
    reportUnknownContact:
      "Report incoming and outgoing messages with numbers not saved in contacts — only the 1st and 2nd message in the chat",
    phoneOwnedSame:
      "This phone number is already linked to another child on your account ({name}). We disconnected this link attempt — use a different number or unlink the existing device first.",
    phoneOwnedOther:
      "This phone number is already linked to another ChatiSafe account. On that phone open WhatsApp → Settings → Linked devices and remove ChatiSafe, then try linking here again.",
    phoneConflictChildTitle: "This number already belongs to a child",
    phoneConflictParentTitle: "This number is a parent",
    phoneBlockedChildSame: "This number is already linked to {name} on this account. It can't be linked again.",
    phoneBlockedChildOther: "This number is already linked to a child on another account. It can't be linked here.",
    phoneBlockedParentSame: "This number is a parent on this account ({name}). It can't be linked as a child.",
    phoneBlockedParentOther: "This number is a parent on another account. It can't be linked as a child.",
    phoneOwnedParentSame: "This number is a parent on this account ({name}). We disconnected the link immediately.",
    phoneOwnedParentOther: "This number is a parent on another account. We disconnected the link immediately.",
    secGeneral: "General reports",
    secGeneralDesc:
      "Filter by media type, alert privacy, and outgoing messages. Content alerts reflect the safety classification level.",
    mediaTypes: "Message types to report",
    reportMediaImages: "Images",
    reportMediaVideos: "Videos",
    reportMediaAudio: "Voice recordings",
    reportMediaText: "Text messages",
    reportMediaStickers: "Stickers",
    privacyMode: "Content in alerts",
    privacyShowContent: "Show reported message content",
    privacyHideContent: "Protect privacy — type of message and content category only",
    privacyHideHint:
      "In privacy mode, alerts omit quotes, images, and audio; you receive a general description of the event.",
    reportOutgoing: "Report harmful outgoing messages your child sent",
    reportOutgoingHint: "Applies to inappropriate content your child sends, for the media types enabled above.",
    classificationGuide:
      "Message levels: OK — no alert. Suspicious — worth a look (not necessarily harmful). Harmful — content that needs your attention.",
    reportSuspicious: "Alerts for suspicious content (not only harmful)",
    reportSuspiciousHint:
      "On by default. When the system is unsure but sees worrying signals (ambiguous wording, missing context), you get a softer WhatsApp heads-up without marking the message as harmful.",
    secForceBlock: "Forced block",
    secForceBlockDesc:
      "Numbers ChatiSafe blocks on your child's WhatsApp. If they remove the block, ChatiSafe puts it back and sends you a separate alert.",
    secWhitelist: "Trusted contacts",
    secWhitelistDesc: "Numbers you trust. Messages with these contacts are not reported to parents.",
    forceBlockAdd: "Block this number",
    whitelistAdd: "Add to list",
    contactPhone: "Phone number",
    contactChild: "Child",
    contactEmpty: "No numbers yet",
    contactRemove: "Remove",
    contactNotConnected: "The child must be connected to WhatsApp before this list can be checked or updated.",
    contactNoChild: "Link a child first.",
    contactSaved: "Number added",
    addFail: "Failed to add child",
    hideFail: "Couldn't hide this child",
    revealFail: "Couldn't show a saved child",
    startFail: "Failed to start",
    unlinkConfirm: "Unlink WhatsApp? This removes the device from WhatsApp Linked Devices.",
    unlinkFail: "Unlink failed — WhatsApp device was NOT removed",
    deleteLinked: "Delete this child? WhatsApp will be unlinked from Linked Devices first.",
    deletePlain: "Delete this child from ChatiSafe?",
    deleteFail: "Delete failed",
    pairFail: "Pairing failed",
    pairNotOnWa: "This number is not registered on WhatsApp.",
    pairInvalidPhone: "Invalid phone number. Check the country code.",
    pairNeedQr: "Still preparing the link — try again in a moment.",
    pairPhoneIntl: "Enter a full number with a country code, not one that starts with 0.",
    parentsRequired: "Add and verify a parent first. Protection stays paused until then.",
    qrFail: "Couldn't show the QR code — try linking again",
    parents: "Parents",
    parentsLead: "Add a parent who receives WhatsApp alerts about harmful messages. Phone verification is required before linking children.",
    addParent: "Add parent",
    parentName: "Parent name",
    parentPhone: "Phone number",
    parentCountry: "Country",
    parentSendCode: "Send verification code",
    parentVerifyLead: "We sent a code on WhatsApp. Enter it here to confirm this number.",
    parentCode: "Verification code",
    parentConfirm: "Confirm & add",
    parentResend: "Resend",
    parentRemove: "Remove",
    parentRemoveConfirm: "Remove this parent from alert recipients?",
    parentInvalidPhone: "Invalid phone number.",
    parentNotOnWa: "This number is not registered on WhatsApp.",
    parentReporterOffline: "Alert service is offline — try again later.",
    parentAlready: "This number is already on your list.",
    parentTakenParentTitle: "This number is already a parent",
    parentTakenParentHere: "This number is already a parent on this account ({name}).",
    parentTakenParentOther: "This number is already a parent on another account. It can't be added again.",
    parentTakenChildTitle: "This number is already a child",
    parentTakenChildHere: "This number is linked to {name} on this account. It can't be added as a parent.",
    parentTakenChildOther: "This number is already linked to a child on another account. It can't be added as a parent.",
    parentWrongCode: "Wrong code.",
    parentCodeExpired: "Code expired — request a new one.",
    parentSendFail: "Could not send verification code.",
    parentGenericFail: "Something went wrong.",
    parentServerError: "Server error — please try again in a moment.",
    parentWaCheckFail: "Couldn't check this number on WhatsApp right now. Try again in a moment.",
    parentWaServiceOutdated: "Alert service is temporarily unavailable. Please try again later.",
    parentNoPending: "No active verification — send a new code.",
    parentTooManyAttempts: "Too many attempts — request a new code.",
    childrenLocked: "Add and verify at least one parent before managing children.",
  },
  common: {
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    back: "Back",
    continue: "Continue",
    confirm: "Confirm",
    delete: "Delete",
    remove: "Remove",
    retry: "Try again",
    loading: "Loading…",
    saved: "Saved",
    saving: "Saving…",
    optional: "Optional",
    showPassword: "Show password",
    hidePassword: "Hide password",
    moreActions: "More actions",
    dismiss: "Dismiss",
    learnMore: "Learn more",
    yes: "Yes",
    no: "No",
    today: "today",
    tomorrow: "tomorrow",
    daysLeft: "{n} days left",
    dayLeft: "1 day left",
    step: "Step {n}",
    of: "of",
    and: "and",
  },
  app: {
    brand: "ChatiSafe",
    documentTitle: "ChatiSafe — WhatsApp protection for kids",
    tabHome: "Home",
    tabParents: "Parents",
    tabParentsShort: "Parents",
    tabParentsHint: "Who gets WhatsApp alerts",
    tabChildren: "Children",
    tabChildrenShort: "Children",
    tabChildrenHint: "The children's phones",
    tabRecipients: "Parents",
    tabRecipientsShort: "Parents",
    tabSettings: "Protection settings",
    tabSettingsShort: "Protection",
    tabSettingsHint: "What to check in messages",
    tabHistory: "History",
    tabHistoryShort: "History",
    tabHistoryHint: "Alerts already sent",
    tabBilling: "Billing",
    tabBillingHint: "Payment and protection",
    account: "My account",
    openAccount: "Account menu",
    signedInAs: "Signed in as",
    navLabel: "Main navigation",
    historyLead: "Every alert sent to the parents. Newest first.",
  },
  home: {
    greeting: "Hello, {name}",
    statusAllOk: "All children are protected",
    statusAllOkSub: "Protection is active for {n} children. If anything worrying happens, you'll get a WhatsApp message.",
    statusOneOk: "{name} is protected",
    statusOneOkSub: "Protection is active. If anything worrying happens, you'll get a WhatsApp message.",
    statusAttention: "{n} children need your attention",
    statusAttentionOne: "One child needs your attention",
    statusAttentionSub: "Handle the items below to get protection running again.",
    statusNone: "Protection isn't active yet",
    statusNoneSub: "Finish the setup steps to protect your first child.",
    statusPaused: "Protection is paused",
    statusPausedSub: "Protection is paused for all children. Resume whenever you're ready.",
    attentionTitle: "Needs attention",
    childrenTitle: "My children",
    childrenLead: "We watch for messages and content that may need your attention.",
    protectOn: "ChatiSafe is active",
    protectWa: "WhatsApp connected",
    protectOff: "WhatsApp is not connected",
    protectNeed: "Action needed",
    connectTitle: "{name}'s WhatsApp still needs to be connected",
    reconnectTitle: "{name}'s WhatsApp disconnected",
    reconnectBody: "To keep ChatiSafe working, connect it again.",
    reconnectBtn: "Connect again",
    emptyConnectTitle: "No child is connected yet",
    emptyConnectBody: "Let's add the first child now. It only takes a few minutes.",
    emptyConnectAction: "Add the first child",
    checklistTitle: "Finish setup",
    checkAccount: "You opened an account",
    checkChild: "You added {name}",
    checkChildPending: "Add a child",
    checkWa: "Connect WhatsApp",
    checkDone: "Finish setup",
    resumeBtn: "Continue where I left off",
    welcomeBanner: "🎉 ChatiSafe is active for {name}",
    dismiss: "Dismiss",
    shareTitle: "Want another parent to get alerts?",
    shareBody: "Add a partner or another parent so they get alerts too.",
    shareBtn: "Add another parent",
    setupTitle: "Three steps and your child is protected",
    setupLead: "Each step takes about a minute. You can stop and come back anytime.",
    setup1: "Add a WhatsApp number for alerts",
    setup1Sub: "This is where alerts are sent. We'll verify it with a short code.",
    setup2: "Add your first child",
    setup2Sub: "3-day free trial, no credit card.",
    setup3: "Link your child's WhatsApp",
    setup3Sub: "Scan a QR code once from the child's phone.",
    setupDone: "Done",
    setupStart: "Start",
    howAlertsTitle: "How will I get alerts?",
    howAlertsBody:
      "When risky content is detected — bullying, threats, strangers or inappropriate media — a WhatsApp message goes to the alert recipients you set. Your child sees nothing.",
    recipientsCount: "{n} recipients",
    recipientsOne: "1 recipient",
    recipientsNone: "No recipients yet",
    manageRecipients: "Manage recipients",
    actNeedsPayment: "Protection for {name} is paused — payment needed",
    actNotLinked: "{name} isn't linked to WhatsApp yet",
    actPaused: "Protection for {name} is paused",
    actPairing: "Waiting for {name}'s QR scan",
    actTrialEnding: "{name}'s trial ends {when}",
    actRenewalFailed: "{name}'s renewal failed — update your card",
    actNoRecipients: "No number for alerts — alerts won't be sent",
    actNoPhone: "{name}'s number hasn't been detected yet",
    payNow: "Pay",
    linkNow: "Link",
    resume: "Resume",
    continueLink: "Continue linking",
    addRecipient: "Add number",
    refresh: "Refresh",
    recentTitle: "What happened recently",
    recentEmpty: "No alerts yet",
    recentEmptySub: "When worrying content is detected, it will show up here and be sent on WhatsApp.",
    recentEmptyProtected: "Protection is on. When worrying content is detected, it will show up here.",
    recentHidden: "Message content is hidden by your privacy settings.",
    incoming: "Received",
    outgoing: "Sent",
    sevUrgent: "Urgent",
    sevSuspicious: "Suspicious",
    sevWarn: "Warning",
    sevWatch: "Watch",
    sevLow: "Low",
    cats: {
      profanity: "Profanity",
      insult: "Insult",
      harassment: "Harassment",
      bullying: "Bullying",
      threat: "Threat",
      violence: "Violence",
      hate: "Hate",
      sexual: "Sexual content",
      sexual_harassment: "Sexual harassment",
      sexual_exploitation: "Sexual exploitation",
      grooming: "Grooming",
      coercion: "Coercion",
      manipulation: "Manipulation",
      humiliation: "Humiliation",
      self_harm: "Self-harm",
      privacy: "Privacy",
      dangerous: "Dangerous content",
      extremism: "Extremism",
    },
  },
  child: {
    stProtected: "Protected",
    stPaused: "Paused",
    stNotLinked: "Not linked",
    stPairing: "Waiting for scan",
    stNeedsPayment: "Payment needed",
    stTrial: "Free trial",
    trialLine: "Free trial · {days}",
    trialUnlimited: "Free trial · no end date",
    activeLine: "Protected until {date}",
    autoOnLine: "Auto-renews on {date}",
    autoOffLine: "No auto-renew · pauses on {date}",
    expiredLine: "Period ended — messages aren't scanned",
    issuePayment: "Protection is paused — payment needed",
    issueRenewal: "Renewal failed — update the card",
    issuePaused: "Protection is paused",
    issueNotLinked: "Not linked to WhatsApp yet",
    issuePairing: "Waiting for a QR scan",
    issueTrialEnding: "Trial ends {when}",
    noneLine: "No active protection period",
    phoneUnknown: "Number is detected after protection starts",
    actLink: "Link WhatsApp",
    actContinueLink: "Continue linking",
    actResume: "Start protection",
    actPause: "Pause",
    actPay: "Pay ₪15",
    actRename: "Rename",
    actUnavailable: "Make unavailable",
    actUnlink: "Unlink WhatsApp",
    actDelete: "Delete child",
    actCancelPair: "Cancel linking",
    deleteLockedHint: "Can't be deleted until the paid period ends",
    renameTitle: "Rename",
    renameLabel: "Child's name",
    renameHint: "The name shown in groups, so we can recognise mentions.",
    unlinkTitle: "Unlink {name}'s WhatsApp?",
    unlinkBody: "The device is removed from Linked Devices on the child's WhatsApp. Protection stops until you link again. The paid period is kept.",
    unlinkConfirm: "Unlink",
    deleteTitle: "Delete {name}?",
    deleteBodyLinked: "WhatsApp is unlinked first, then the child is removed from your account. This can't be undone.",
    deleteBodyPlain: "The child is removed from your account. This can't be undone.",
    deleteConfirm: "Delete",
    addChild: "Add child",
    addNeedsLinkTitle: "Can't add a child yet",
    addNeedsLink: "A child still isn't linked to WhatsApp. Link them before adding another.",
    emptyTitle: "No children yet",
    emptyBody: "Add your first child — 3-day free trial, no credit card — then link their WhatsApp by scanning a QR code.",
    lockedTitle: "Add a parent first",
    lockedBody: "Without a verified parent there is no protection and no reports. Add your number on the Parents page.",
    lockedAction: "Go to Parents",
  },
  link: {
    title: "Link {name}'s WhatsApp",
    lead: "On your child's phone:",
    step1: "Open WhatsApp → ⋮ or Settings",
    step2: "Linked devices → Link a device",
    step3: "Scan the code shown here",
    waitingQr: "Preparing the QR code…",
    qrAlt: "QR code to link WhatsApp",
    qrExpiresHint: "The code refreshes automatically. No need to reload.",
    codeToggle: "Can't scan? Link with a code",
    codeLead: "Enter the child's WhatsApp number. If it exists, we'll show a code here to type on their phone (not sent by SMS).",
    phoneLabel: "Child's WhatsApp number",
    phonePh: "050-000-0000",
    getCode: "Show linking code",
    checkingWa: "Checking if the number is on WhatsApp…",
    codeReady: "On the child's phone: Linked devices → Link with phone number → type this code:",
    connectedTitle: "{name} is linked!",
    connectedBody: "Protection is on. You can close this window.",
    cancel: "Cancel linking",
    mockNote: "Development mode — sample QR, not scannable.",
    mockScan: "Simulate scan",
  },
  recipients: {
    title: "Parents",
    lead: "Parents listed here get WhatsApp alerts.",
    add: "Add parent",
    empty: "No parents yet",
    emptyBody: "Add your number (or your partner's). Only after verification can you add children and turn protection on.",
    addTitle: "Add a parent",
    addLead: "We'll send a short verification code on WhatsApp to this number.",
    verifyTitle: "Verify the number",
    verifyLead: "We sent a code on WhatsApp to {phone}. Enter it here.",
    nameLabel: "Name",
    namePh: "e.g. Mum, Dad, Dana",
    phoneLabel: "WhatsApp number",
    sendCode: "Send code",
    confirm: "Confirm & add",
    resend: "Resend code",
    changeNumber: "Change number",
    removeTitle: "Remove {name}?",
    removeBody: "{phone} will stop receiving alerts.",
    removeConfirm: "Remove",
    verified: "Verified",
  },
  errors: {
    notFoundTitle: "Page not found",
    notFoundBody: "The link you followed doesn't exist or has moved.",
    errorTitle: "Something went wrong",
    errorBody: "An unexpected error occurred. Try refreshing — if it keeps happening, let us know.",
    goHome: "Go to homepage",
    goDashboard: "Go to dashboard",
    tryAgain: "Try again",
  },
  admin: {
    title: "Admin",
    password: "Admin password",
    enter: "Enter",
    wrong: "Wrong password",
    users: "Users",
    payments: "Payments",
    sessions: "Sessions",
    logout: "Log out",
    all: "All",
    paying: "Paying",
    notPaying: "Not paying",
    noUsers: "No users",
    noSessions: "No sessions",
    noPayments: "No paying users",
    name: "Name",
    email: "Email",
    phone: "Phone",
    plan: "Plan",
    none: "None",
    child: "Child",
    session: "Session",
    waPhone: "Connected number",
    notConnected: "Not connected",
    running: "Running",
    stopped: "Stopped",
    connected: "Connected",
    owner: "Owner",
    registered: "Registered",
    expires: "Expires",
    count: "{n} users",
  },
};

export type Dict = typeof en;
export const dictionaries: Record<Locale, Dict> = { he: he as Dict, en };

/** Browser tab title for the current site language. */
export function tabTitle(locale: Locale, pathname: string) {
  const t = dictionaries[locale] ?? dictionaries[DEFAULT_LOCALE];
  const brand = t.app.brand;
  if (pathname === "/terms" || pathname.startsWith("/terms/")) return `${t.legal.terms} · ${brand}`;
  if (pathname === "/privacy" || pathname.startsWith("/privacy/")) return `${t.legal.privacy} · ${brand}`;
  if (pathname === "/accessibility" || pathname.startsWith("/accessibility/")) return `${t.legal.accessibility} · ${brand}`;
  return t.app.documentTitle;
}

export function interpolate(template: string, vars: Record<string, string | number>) {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));
}
